"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Channel = void 0;
var browser_clip_1 = require("./browser-clip");
var utils_1 = require("./utils");
/**
 * Get the next logical position to play in the session
 * Tone has a build-in method `Tone.Transport.nextSubdivision('4n')`
 * but I think it s better to round off as follows for live performance
 */
var getNextPos = function (clip) {
    // TODO: (soon) convert to using transportPosTicks (fewer computations)
    var arr = Tone.Transport.position.split(':');
    // If we are still around 0:0:0x, then set start position to 0
    if (arr[0] === '0' && arr[1] === '0') {
        return 0;
    }
    // Else set it to the next bar
    var transportPosTicks = Tone.Transport.ticks;
    var align = (clip === null || clip === void 0 ? void 0 : clip.align) || '1m';
    var alignOffset = (clip === null || clip === void 0 ? void 0 : clip.alignOffset) || '0';
    var alignTicks = Tone.Ticks(align).toTicks();
    var alignOffsetTicks = Tone.Ticks(alignOffset).toTicks();
    var nextPosTicks = Tone.Ticks(Math.floor(transportPosTicks / alignTicks + 1) * alignTicks +
        alignOffsetTicks);
    // const nextPosBBS = nextPosTicks.toBarsBeatsSixteenths();
    // return nextPosBBS; // Extraneous computations
    return nextPosTicks;
};
/**
 * Channel
 * A channel is made up of a Tone.js Player/Instrument, one or more
 * Tone.js sequences (known as clips in Scribbletune)
 * & optionally a set of effects (with or without presets)
 *
 * API:
 * clips -> Get all clips for this channel
 * addClip -> Add a new clip to the channel
 * startClip -> Start a clip at the provided index
 * stopClip -> Stop a clip at the provided index
 * activeClipIdx -> Get the clip that is currently playing
 */
var Channel = /** @class */ (function () {
    function Channel(params) {
        var _this = this;
        this.idx = params.idx || 0;
        this.name = params.name || 'ch ' + params.idx;
        this.activePatternIdx = -1;
        this.channelClips = [];
        this.clipNoteCount = 0;
        // Filter out unrequired params and create clip params object
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        var clips = params.clips, samples = params.samples, sample = params.sample, synth = params.synth, params1 = __rest(params, ["clips", "samples", "sample", "synth"]);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        var external = params1.external, sampler = params1.sampler, buffer = params1.buffer, params2 = __rest(params1, ["external", "sampler", "buffer"]);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        var player = params2.player, instrument = params2.instrument, volume = params2.volume, params3 = __rest(params2, ["player", "instrument", "volume"]);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        var eventCb = params3.eventCb, playerCb = params3.playerCb, effects = params3.effects, params4 = __rest(params3, ["eventCb", "playerCb", "effects"]);
        var _a = params4.context, context = _a === void 0 ? Tone.getContext() : _a, originalParamsFiltered = __rest(params4, ["context"]);
        this.eventCbFn = eventCb;
        this.playerCbFn = playerCb;
        // Async section
        this.hasLoaded = false;
        this.hasFailed = false;
        this.initializerTask = this.initOutputProducer(context, params).then(function () {
            return _this.initInstrument(context, params).then(function () {
                return _this.adjustInstrument(context, params).then(function () {
                    return _this.initEffects(context, params);
                });
            });
        });
        // End Async section
        // Sync section
        var clipsFailed = false;
        try {
            params.clips.forEach(function (c, i) {
                try {
                    _this.addClip(__assign(__assign({}, c), originalParamsFiltered));
                }
                catch (e) {
                    // Annotate the error with Clip info
                    throw new Error(((0, utils_1.errorHasMessage)(e) ? e.message : e) + " in clip " + (i + 1));
                }
            }, this);
        }
        catch (e) {
            clipsFailed = e; // Stash the error
        }
        // End Sync section
        // Reconcile sync section with async section
        this.initializerTask
            .then(function () {
            if (clipsFailed) {
                throw clipsFailed;
            }
            _this.hasLoaded = true;
            _this.eventCb('loaded', {}); // Report async load completion.
        })
            .catch(function (e) {
            _this.hasFailed = e;
            _this.eventCb('error', { e: e }); // Report async errors.
        });
    }
    Channel.setTransportTempo = function (valueBpm) {
        Tone.Transport.bpm.value = valueBpm;
    };
    Channel.startTransport = function () {
        Tone.start();
        Tone.Transport.start();
    };
    Channel.stopTransport = function (deleteEvents) {
        if (deleteEvents === void 0) { deleteEvents = true; }
        Tone.Transport.stop();
        if (deleteEvents) {
            // Delete all events in the Tone.Transport
            Tone.Transport.cancel();
        }
    };
    Channel.prototype.setVolume = function (volume) {
        // ? this.volume = volume;
        var _a, _b;
        // Change volume of the player
        // if (this.player) {
        //   this.player.volume.value = volume;
        // }
        // Change volume of the sampler
        // if (this.sampler) {
        //   this.sampler.volume.value = volume;
        // }
        // Change volume of the instrument
        if (this.instrument) {
            this.instrument.volume.value = volume;
        }
        // Change volume of the external
        if (this.external) {
            (_b = (_a = this.external).setVolume) === null || _b === void 0 ? void 0 : _b.call(_a, volume);
        }
    };
    Channel.prototype.startClip = function (idx, position) {
        var _this = this;
        var clip = this.channelClips[idx];
        position = position || (position === 0 ? 0 : getNextPos(clip));
        // Stop any other currently running clip
        if (this.activePatternIdx > -1 && this.activePatternIdx !== idx) {
            this.stopClip(this.activePatternIdx, position);
        }
        if (clip && clip.state !== 'started') {
            // We need to schedule that for just before when clip?.start(position) events start coming.
            this.counterResetTask = Tone.Transport.scheduleOnce(function ( /* time: Tone.Seconds */) {
                _this.clipNoteCount = 0;
            }, position);
            this.activePatternIdx = idx;
            // clip?.stop(position); // DEBUG: trying to clear out start/stop events
            // clip?.clear(position); // DEBUG: trying to clear out events
            clip === null || clip === void 0 ? void 0 : clip.start(position);
        }
    };
    Channel.prototype.stopClip = function (idx, position) {
        var clip = this.channelClips[idx];
        position = position || (position === 0 ? 0 : getNextPos(clip));
        clip === null || clip === void 0 ? void 0 : clip.stop(position);
        if (idx === this.activePatternIdx) {
            this.activePatternIdx = -1;
        }
    };
    Channel.prototype.addClip = function (clipParams, idx) {
        var _this = this;
        idx = idx || this.channelClips.length;
        if (clipParams.pattern) {
            this.channelClips[idx] = (0, browser_clip_1.clip)(__assign({}, clipParams), this);
            // Pass certain clipParams into getNextPos()
            ['align', 'alignOffset'].forEach(function (key) {
                if (clipParams[key]) {
                    _this.channelClips[idx][key] = clipParams[key];
                }
            });
        }
        else {
            // Allow creation of empty clips
            this.channelClips[idx] = null;
        }
    };
    /**
     * @param  {Object} ClipParams clip parameters
     * @return {Function} function that can be used as the callback in Tone.Sequence https://tonejs.github.io/docs/Sequence
     */
    Channel.prototype.getSeqFn = function (params) {
        var _this = this;
        if (this.external) {
            return function (time, el) {
                var _a, _b;
                if (el === 'x' || el === 'R') {
                    var counter = _this.clipNoteCount;
                    if (_this.hasLoaded) {
                        var note = (0, browser_clip_1.getNote)(el, params, counter)[0];
                        var duration = (0, browser_clip_1.getDuration)(params, counter);
                        var durSeconds = Tone.Time(duration).toSeconds();
                        _this.playerCb({ note: note, duration: duration, time: time, counter: counter });
                        try {
                            (_b = (_a = _this.external).triggerAttackRelease) === null || _b === void 0 ? void 0 : _b.call(_a, note, durSeconds, time);
                        }
                        catch (e) {
                            _this.eventCb('error', { e: e }); // Report play errors.
                        }
                    }
                    _this.clipNoteCount++;
                }
            };
        }
        else if (this.instrument instanceof Tone.Player) {
            return function (time, el) {
                if (el === 'x' || el === 'R') {
                    var counter = _this.clipNoteCount;
                    if (_this.hasLoaded) {
                        _this.playerCb({ note: '', duration: '', time: time, counter: counter });
                        try {
                            _this.instrument.start(time);
                        }
                        catch (e) {
                            _this.eventCb('error', { e: e }); // Report play errors.
                        }
                    }
                    _this.clipNoteCount++;
                }
            };
        }
        else if (this.instrument instanceof Tone.PolySynth ||
            this.instrument instanceof Tone.Sampler) {
            return function (time, el) {
                if (el === 'x' || el === 'R') {
                    var counter = _this.clipNoteCount;
                    if (_this.hasLoaded) {
                        var note = (0, browser_clip_1.getNote)(el, params, counter);
                        var duration = (0, browser_clip_1.getDuration)(params, counter);
                        _this.playerCb({ note: note, duration: duration, time: time, counter: counter });
                        try {
                            _this.instrument.triggerAttackRelease(note, duration, time);
                        }
                        catch (e) {
                            _this.eventCb('error', { e: e }); // Report play errors.
                        }
                    }
                    _this.clipNoteCount++;
                }
            };
        }
        else if (this.instrument instanceof Tone.NoiseSynth) {
            return function (time, el) {
                if (el === 'x' || el === 'R') {
                    var counter = _this.clipNoteCount;
                    if (_this.hasLoaded) {
                        var duration = (0, browser_clip_1.getDuration)(params, counter);
                        _this.playerCb({ note: '', duration: duration, time: time, counter: counter });
                        try {
                            _this.instrument.triggerAttackRelease(duration, time);
                        }
                        catch (e) {
                            _this.eventCb('error', { e: e }); // Report play errors.
                        }
                    }
                    _this.clipNoteCount++;
                }
            };
        }
        else {
            return function (time, el) {
                if (el === 'x' || el === 'R') {
                    var counter = _this.clipNoteCount;
                    if (_this.hasLoaded) {
                        var note = (0, browser_clip_1.getNote)(el, params, counter)[0];
                        var duration = (0, browser_clip_1.getDuration)(params, counter);
                        _this.playerCb({ note: note, duration: duration, time: time, counter: counter });
                        try {
                            _this.instrument.triggerAttackRelease(note, duration, time);
                        }
                        catch (e) {
                            _this.eventCb('error', { e: e }); // Report play errors.
                        }
                    }
                    _this.clipNoteCount++;
                }
            };
        }
    };
    Channel.prototype.eventCb = function (event, params) {
        if (typeof this.eventCbFn === 'function') {
            params.channel = this;
            this.eventCbFn(event, params);
        }
    };
    Channel.prototype.playerCb = function (params) {
        if (typeof this.playerCbFn === 'function') {
            params.channel = this;
            this.playerCbFn(params);
        }
    };
    /**
     * Check Tone.js object loaded state and either invoke `resolve` right away, or attach to and wait using Tone onload cb.
     * It's an ugly hack that reaches into Tone's internal ._buffers or ._buffer to insert itself into .onload() callback.
     * Tone has different ways to pull the onload callback from within to the API, so this implementation is very brittle.
     * The sole reason for its existence is to handle async loaded state of Tone instruments that we allow to pass in from outside.
     * If that option is eliminated, then this hacky function can be killed (or re-implemented via public onload API)
     * @param toneObject Tone.js object (will work with non-Tone objects that have same loaded/onload properties)
     * @param resolve onload callback
     */
    Channel.prototype.checkToneObjLoaded = function (toneObject, resolve) {
        var _this = this;
        var skipRecursion = toneObject instanceof Tone.Sampler; // Sampler has a Map of ToneAudioBuffer, and our method to find inner .onload() does not work since there is no single one.
        // eslint-disable-next-line no-prototype-builtins
        if ('loaded' in toneObject) {
            if (toneObject.loaded) {
                resolve();
                return;
            }
            if (skipRecursion) {
                return;
            }
            // Try Recursion into inner objects:
            var handled_1 = false;
            ['buffer', '_buffer', '_buffers'].forEach(function (key) {
                if (key in toneObject) {
                    _this.checkToneObjLoaded(toneObject[key], resolve);
                    handled_1 = true;
                }
            });
            if (handled_1) {
                return;
            }
        }
        // Check object type if it has load/onload (and _buffers or _buffer), then call resolve()
        // The list was created for Tone@14.8.0 by grepping and reviewing the source code.
        // Known objecs to have:
        var hasOnload = toneObject instanceof Tone.ToneAudioBuffer ||
            toneObject instanceof Tone.ToneBufferSource ||
            // Falback for "future" objects
            ('loaded' in toneObject && 'onload' in toneObject);
        if (!hasOnload) {
            // console.log('resolve() for ch "%o" idx %o onload NOT FOUND', this.name, this.idx);
            // This is not a good assumption. E.g. it does not work for Tone.ToneAudioBuffers
            resolve();
        }
        else {
            var oldOnLoad_1 = toneObject.onload;
            toneObject.onload = function () {
                if (oldOnLoad_1 && typeof oldOnLoad_1 === 'function') {
                    toneObject.onload = oldOnLoad_1;
                    oldOnLoad_1();
                }
                // console.log('resolve() for ch "%o" idx %o', this.name, this.idx);
                resolve();
            };
        }
    };
    Channel.prototype.recreateToneObjectInContext = function (toneObject, // Tone.PolySynth | Tone.Player | Tone.Sampler | Tone['' | '']
    context) {
        var _this = this;
        context = context || Tone.getContext();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        return new Promise(function (resolve, reject) {
            // Tone.PolySynth | Tone.Player | Tone.Sampler | Tone['' | '']
            if (toneObject instanceof Tone.PolySynth) {
                var newObj_1 = Tone.PolySynth(Tone[toneObject._dummyVoice.name], __assign(__assign({}, toneObject.get()), { context: context }));
                _this.checkToneObjLoaded(newObj_1, function () { return resolve(newObj_1); });
            }
            else if (toneObject instanceof Tone.Player) {
                var newObj_2 = Tone.Player({
                    url: toneObject._buffer,
                    context: context,
                    onload: function () { return _this.checkToneObjLoaded(newObj_2, function () { return resolve(newObj_2); }); },
                });
            }
            else if (toneObject instanceof Tone.Sampler) {
                var _a = toneObject.get(), attack = _a.attack, curve = _a.curve, release = _a.release, volume = _a.volume;
                var paramsFromSampler = {
                    attack: attack,
                    curve: curve,
                    release: release,
                    volume: volume,
                };
                var paramsFromBuffers = {
                    baseUrl: toneObject._buffers.baseUrl,
                    urls: Object.fromEntries(toneObject._buffers._buffers.entries()),
                };
                var newObj_3 = Tone.Sampler(__assign(__assign(__assign({}, paramsFromSampler), paramsFromBuffers), { context: context, onload: function () { return _this.checkToneObjLoaded(newObj_3, function () { return resolve(newObj_3); }); } }));
            }
            else {
                var newObj_4 = Tone[toneObject.name](__assign(__assign({}, toneObject.get()), { context: context, onload: function () { return _this.checkToneObjLoaded(newObj_4, function () { return resolve(newObj_4); }); } }));
                _this.checkToneObjLoaded(newObj_4, function () { return resolve(newObj_4); });
            }
        });
    };
    Channel.prototype.initOutputProducer = function (context, params) {
        var _this = this;
        context = context || Tone.getContext();
        return new Promise(function (resolve, reject) {
            /*
             *  1. The params object can be used to pass a sample (sound source) OR a synth(Synth/FMSynth/AMSynth etc) or samples.
             *  Scribbletune will then create a Tone.js Player or Tone.js Instrument or Tone.js Sampler respectively
             *  2. It can also be used to pass a Tone.js Player object or instrument that was created elsewhere
             *  (mostly by Scribbletune itself in the channel creation method)
             **/
            if (params.synth) {
                if (params.instrument) {
                    throw new Error('Either synth or instrument can be provided, but not both.');
                }
                if (params.synth.synth) {
                    var synthName = params.synth.synth;
                    //  const presetName = (params.synth as SynthParams).presetName; // Unused here
                    var preset = params.synth.preset || {};
                    _this.instrument = new Tone[synthName](__assign(__assign({}, preset), { context: context, 
                        // Use onload for cases when synthName calls out Tone.Sample/Player/Sampler.
                        // It could be a universal way to load Tone.js instruments.
                        onload: function () { return _this.checkToneObjLoaded(_this.instrument, resolve); } }));
                    _this.checkToneObjLoaded(_this.instrument, resolve);
                }
                else {
                    _this.instrument = params.synth; // TODO: This is dangerous by-reference assignment.
                    console.warn('The "synth" parameter with instrument will be deprecated in the future. Please use the "instrument" parameter instead.');
                    // params.synth describing the Tone[params.synth.synth] is allowed.
                    _this.checkToneObjLoaded(_this.instrument, resolve);
                }
            }
            else if (typeof params.instrument === 'string') {
                _this.instrument = new Tone[params.instrument]({ context: context });
                _this.checkToneObjLoaded(_this.instrument, resolve);
            }
            else if (params.instrument) {
                _this.instrument = params.instrument; // TODO: This is dangerous by-reference assignment. Tone.instrument has context that holds all other instruments. Client side params get polluted with circular references. If params come from e.g. react-ApolloClient data, Apollo tools crash on circular references.
                _this.checkToneObjLoaded(_this.instrument, resolve);
            }
            else if (params.sample || params.buffer) {
                _this.instrument = new Tone.Player({
                    url: params.sample || params.buffer,
                    context: context,
                    onload: function () { return _this.checkToneObjLoaded(_this.instrument, resolve); },
                });
            }
            else if (params.samples) {
                _this.instrument = new Tone.Sampler({
                    urls: params.samples,
                    context: context,
                    onload: function () { return _this.checkToneObjLoaded(_this.instrument, resolve); },
                });
            }
            else if (params.sampler) {
                _this.instrument = params.sampler; // TODO: This is dangerous by-reference assignment.
                _this.checkToneObjLoaded(_this.instrument, resolve);
            }
            else if (params.player) {
                _this.instrument = params.player; // TODO: This is dangerous by-reference assignment.
                _this.checkToneObjLoaded(_this.instrument, resolve);
            }
            else if (params.external) {
                _this.external = __assign({}, params.external); // Sanitize object by shallow clone
                _this.instrument = {
                    context: context,
                    volume: { value: 0 },
                };
                // Do not call! this.checkToneObjLoaded(this.instrument, resolve);
                if (params.external.init) {
                    return params.external
                        .init(context.rawContext)
                        .then(function () {
                        resolve();
                    })
                        .catch(function (e) {
                        var _a;
                        reject(new Error(e.message + " loading external output module of channel idx " + _this.idx + ", " + ((_a = _this.name) !== null && _a !== void 0 ? _a : '(no name)')));
                    });
                }
                else {
                    resolve();
                }
            }
            else {
                throw new Error('One of required synth|instrument|sample|sampler|samples|buffer|player|external is not provided!');
            }
            if (!_this.instrument) {
                throw new Error('Failed instantiating instrument from given params.');
            }
        });
    };
    Channel.prototype.initInstrument = function (context, params) {
        var _this = this;
        var _a;
        context = context || Tone.getContext();
        if (!params.external && ((_a = this.instrument) === null || _a === void 0 ? void 0 : _a.context) !== context) {
            return this.recreateToneObjectInContext(this.instrument, context).then(function (newObj) {
                _this.instrument = newObj;
            });
        }
        else {
            // Nothing to do
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            return new Promise(function (resolve, reject) {
                resolve();
            });
        }
    };
    Channel.prototype.adjustInstrument = function (context, params) {
        var _this = this;
        context = context || Tone.getContext();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        return new Promise(function (resolve, reject) {
            if (params.volume) {
                // this.instrument.volume.value = params.volume;
                _this.setVolume(params.volume);
            }
            resolve();
        });
    };
    Channel.prototype.initEffects = function (context, params) {
        var _this = this;
        context = context || Tone.getContext();
        var createEffect = function (effect) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            return new Promise(function (resolve, reject) {
                if (typeof effect === 'string') {
                    resolve(new Tone[effect]({ context: context }));
                }
                else if (effect.context !== context) {
                    return _this.recreateToneObjectInContext(effect, context);
                }
                else {
                    resolve(effect);
                }
            }).then(function (effectOut) {
                return effectOut.toDestination();
            });
        };
        var startEffect = function (eff) {
            return typeof eff.start === 'function' ? eff.start() : eff;
        };
        var toArray = function (someVal) {
            if (!someVal) {
                return [];
            }
            if (Array.isArray(someVal)) {
                return someVal;
            }
            return [someVal];
        };
        var effectsIn = toArray(params.effects);
        if (params.external) {
            if (effectsIn.length !== 0) {
                throw new Error('Effects cannot be used with external output');
            }
            return Promise.resolve();
        }
        // effects = params.effects.map(createEffect).map(startEffect);
        return Promise.all(effectsIn.map(createEffect))
            .then(function (results) { return results.map(startEffect); })
            .then(function (effects) {
            var _a;
            (_a = _this.instrument).chain.apply(_a, effects).toDestination();
        });
    };
    Object.defineProperty(Channel.prototype, "clips", {
        get: function () {
            return this.channelClips;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Channel.prototype, "activeClipIdx", {
        get: function () {
            return this.activePatternIdx;
        },
        enumerable: false,
        configurable: true
    });
    return Channel;
}());
exports.Channel = Channel;
